import React, {useEffect, useRef, useState} from "react";
import {
  View, TextInput, StyleProp, ViewStyle, Platform, Pressable, NativeSyntheticEvent, TextInputFocusEventData, TextStyle,
  ColorValue
} from "react-native";
import MainStyle from "../../MainStyle";
import Ionicons from '@expo/vector-icons/Ionicons';
import {toCapitalize} from "../utils";
import {outline_0} from "../constants";
import {colors} from "../assets/styles/colors";
import Div from "../lib/Div";
import P from "../lib/P";
import {FormikErrors} from "formik";

// @ts-ignore
import NumericInput from "@wwdrew/react-native-numeric-textinput";

type Props = {
  style?: StyleProp<ViewStyle>, placeholder?: string, label?: string, type?: 'numeric' | 'email-address',
  secureTextEntry?: boolean, helperText?: string, value?: string, onChangeText?: ((text: string) => void),
  onBlur?: ((e: NativeSyntheticEvent<TextInputFocusEventData>) => void),
  error?: string | boolean | null | string[] | FormikErrors<any> | FormikErrors<any>[], autoFocus?: boolean,
  editable?: boolean, dollarMask?: boolean, labelStyle?: StyleProp<TextStyle>, inputStyles?: StyleProp<TextStyle>,
  decimalPlaces?: number, useGrouping?: boolean, onFocusScroll?: boolean, size?: 'small', errorMessageTop?: boolean,
  mb?: ViewStyle['marginBottom'], w?: ViewStyle['width'], textAlign?: 'auto' | 'left' | 'right' | 'center' | 'justify',
  showErrorMessage?: boolean, placeholderTextColor?: string, selection?: { start: number; end?: number | undefined },
  mx?: ViewStyle['marginHorizontal'], h?: TextStyle['height'], rounded?: number, borderColor?: ColorValue,
  bg?: ColorValue, fs?: number, px?: TextStyle['paddingHorizontal'], color?: ColorValue
};

const Input = (props: Props) => {
  const {style, placeholder, label, type, secureTextEntry, helperText, value, error, autoFocus, mb, editable} = props;
  const {labelStyle, inputStyles, dollarMask, decimalPlaces, useGrouping, onFocusScroll, w, textAlign, px} = props;
  const {showErrorMessage = true, placeholderTextColor, selection, mx, h, rounded, borderColor, bg, fs, color} = props;
  let wrapStyle: StyleProp<ViewStyle> = [MainStyle.relative];
  const [secureText, setSecureText] = useState(secureTextEntry);
  let inputStyle: StyleProp<TextStyle> = [
    outline_0, MainStyle.b_r_200, MainStyle.b_w_1, MainStyle.b_c_settings_border, MainStyle.bg_loading_login,
    MainStyle.h_66, MainStyle.p_x_32, MainStyle.c_white, MainStyle.Barlow500, MainStyle.f_s_24
  ];
  const wrapRef = useRef<any>();
  const secureTextStyle: any = (secureText && Platform.OS === 'web') ? {WebkitTextSecurity: 'disc'} : {};

  if (style) {
    if (Array.isArray(style)) wrapStyle = [...wrapStyle, ...style];
    else wrapStyle.push(style);
  }

  if (mb !== undefined) wrapStyle.push({marginBottom: mb});
  if (mx !== undefined) wrapStyle.push({marginHorizontal: mx});
  if (w !== undefined) wrapStyle.push({width: w});
  if (textAlign) inputStyle.push({textAlign});
  if (h !== undefined) inputStyle.push({height: h});
  if (rounded !== undefined) inputStyle.push({borderRadius: rounded});
  if (borderColor) inputStyle.push({borderColor});
  if (bg) inputStyle.push({backgroundColor: bg});
  if (fs !== undefined) inputStyle.push({fontSize: fs});
  if (px !== undefined) inputStyle.push({paddingHorizontal: px});
  if (color) inputStyle.push({color});

  const onTouchStart = () => {
    if (Platform.OS === 'web') {
      const {maxTouchPoints, platform} = window.navigator;

      if (maxTouchPoints && platform === 'Win32' && onFocusScroll) {
        wrapRef.current?.scrollIntoView({behavior: 'instant', block: 'center', inline: 'center'});
      }
    }
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      const {maxTouchPoints, platform} = window.navigator;

      if ((dollarMask || secureTextEntry) && maxTouchPoints && platform === 'Win32') {
        wrapRef.current?.querySelector?.('input')?.setAttribute('type', 'tel');
      }
    }
  }, []);

  return (
    <View style={wrapStyle}>
      {(label || helperText) && (
        <Div row px={9} mb={3} position="relative">
          {label && <P ta="center" fw="500" fs={14} lh={18} flex={1} opacity={0.5} style={labelStyle}>{label}</P>}
          {helperText ? <P flex={1} ta="right" fw="500" fs={12}>{helperText}</P> : null}
        </Div>
      )}

      <View style={MainStyle.relative} ref={ref => wrapRef.current = ref} onTouchStart={onTouchStart}>
        {dollarMask ? (
          <NumericInput
            value={value} style={[inputStyle, inputStyles, secureTextStyle, error && MainStyle.b_c_warn]}
            onUpdate={(val: any) => {
              if (props.onChangeText) props.onChangeText(`${val ?? ''}`);
            }}
            placeholder={placeholder} placeholderTextColor={placeholderTextColor || colors.gray}
            decimalPlaces={decimalPlaces || 0} secureTextEntry={Platform.OS !== 'web' && secureText}
            onBlur={props.onBlur} autoFocus={autoFocus} editable={editable} useGrouping={!!useGrouping}
          />
        ) : (
          <TextInput
            placeholder={placeholder} style={[inputStyle, inputStyles, secureTextStyle, error && MainStyle.b_c_warn]}
            placeholderTextColor={placeholderTextColor || colors.gray} keyboardType={type} secureTextEntry={secureText}
            value={value} onChangeText={props.onChangeText} onBlur={props.onBlur} autoFocus={autoFocus}
            editable={editable} selection={selection}
          />
        )}

        {typeof secureText === 'boolean' && (
          <Div position="absolute" bottom={1} top={1} right={1} row bg="transparent">
            <Pressable style={styles.icon_s} onPress={() => props.onChangeText?.('')}>
              <Ionicons name="close" size={16} color={colors.iconColor}/>
            </Pressable>

            <Pressable style={styles.icon_s} onPress={() => setSecureText(!secureText)}>
              <Ionicons name={secureText ? 'eye-off-outline' : 'eye-outline'} size={16} color={colors.iconColor}/>
            </Pressable>
          </Div>
        )}
      </View>

      {showErrorMessage && (
        <Div position="absolute" bottom={-14} w="100%">
          {error && (
            <P flex={1} ta="center" fw="500" fs={12} color={colors.newDangerText}>{toCapitalize(error as string)}</P>
          )}
        </Div>
      )}
    </View>
  );
};

const styles = {
  icon_s: [MainStyle.h_40, MainStyle.w_40, MainStyle.a_i_c, MainStyle.j_c_c]
};

export default React.memo(Input);
